import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Select, Space, Table, Typography } from 'antd';
import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import recognitionService, { type RecognitionRun } from '@/services/financeRecognition.service';
import { StatusTag, amount, apiErrorMessage, humanize, options, useRecognitionAccess } from './prepaidDisplay';

const { Title } = Typography;

const RUN_STATUSES = ['DRAFT', 'VALIDATED', 'PROCESSING', 'PARTIALLY_COMPLETED', 'COMPLETED', 'FAILED'];

const PrepaidRunsPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const access = useRecognitionAccess();
  const [status, setStatus] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['recognition', 'runs', status, page, pageSize],
    queryFn: () => recognitionService.listRuns({ status, page, limit: pageSize }),
    placeholderData: keepPreviousData,
    enabled: access.canView,
  });

  if (!access.canView) return <Alert type="warning" showIcon style={{ margin: 24 }} message={t('noAccessRecognitionRuns')} />;

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/fin/prepaid')}>
            {t('workspace')}
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            {t('recognitionRuns')}
          </Title>
        </Space>
        <Space>
          <Select allowClear placeholder={tc('status')} style={{ width: 200 }} value={status} options={options(RUN_STATUSES)} onChange={(v) => { setStatus(v); setPage(1); }} />
          {access.canRun && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/fin/prepaid/runs/new')}>
              {t('newRun')}
            </Button>
          )}
        </Space>
      </Space>
      {isError && <Alert type="error" showIcon message={apiErrorMessage(error)} style={{ marginBottom: 12 }} />}
      <Table<RecognitionRun>
        size="small"
        rowKey="id"
        loading={isLoading}
        dataSource={data?.data ?? []}
        pagination={{
          current: page,
          pageSize,
          total: data?.total ?? 0,
          showSizeChanger: true,
          onChange: (p, s) => {
            setPage(p);
            setPageSize(s);
          },
        }}
        columns={[
          { title: t('run'), dataIndex: 'run_number', render: (v: string, r) => <Link to={`/fin/prepaid/runs/${r.id}`}>{v}</Link> },
          { title: t('throughDate'), dataIndex: 'recognition_through_date', width: 120 },
          { title: t('actionCol'), dataIndex: 'posting_action', width: 100, render: (v: string) => (v === 'DRAFT' ? 'Draft JVs' : 'Post') },
          { title: t('granularity'), dataIndex: 'posting_granularity', width: 220, render: (v: string) => humanize(v) },
          { title: t('lines'), dataIndex: 'item_count', width: 80, align: 'right' },
          { title: t('eligibleBase'), dataIndex: 'eligible_amount', width: 140, align: 'right', render: (v: string) => amount(v) },
          { title: t('postedBase'), dataIndex: 'posted_amount', width: 140, align: 'right', render: (v: string) => amount(v) },
          { title: t('exceptions'), dataIndex: 'exception_count', width: 100, align: 'right' },
          { title: t('triggeredBy'), dataIndex: 'triggered_by', width: 110, render: (v: string, r) => (v === 'USER' ? r.created_by_name || 'User' : humanize(v)) },
          { title: t('created'), dataIndex: 'created_at', width: 150, render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm') },
          { title: tc('status'), dataIndex: 'status', width: 160, render: (s: string) => <StatusTag status={s} /> },
        ]}
      />
    </div>
  );
};

export default PrepaidRunsPage;
